import { useCallback, useEffect, useRef, useState } from 'react'
import { getFromStorage, saveToStorage, trackUserEvent } from '@vend/utilities'
import {
  getUserAttributes,
  setUserAttribute,
  UserAttribute,
} from '../../../users/users-resource/users-resource'
import { useUserManager } from '../../../../ng-compat/hooks'
import { WEB_REGISTER } from '../../../web-register.constant'

export const LSPAY_CNP_ONBOARDING_ATTRIBUTE = 'show_lspay_cnp_onboarding' as const

export const LSPAY_CNP_ONBOARDING_DISMISSED_VALUE = 'false' as const

// Local storage key is user-scoped so multiple users on the same device are
// handled correctly. Once set, the API read is skipped on subsequent opens.
export const getLspayCnpOnboardingLocalKey = (userId: string) =>
  `lspay_cnp_onboarding_dismissed_${userId}`

const LSPAY_CNP_SIGNPOST_OPENED_EVENT = 'LSPay CNP Split Signpost Opened'
const LSPAY_CNP_SIGNPOST_DISMISSED_EVENT = 'LSPay CNP Split Signpost Dismissed'
const LSPAY_CNP_SIGNPOST_DISMISSAL_FAILED_EVENT =
  'LSPay CNP Split Signpost Dismissal Failed'

type LSPayCnpSplitSignpostSource = 'cpModal' | 'isolated'

interface UseLspayCnpOnboardingSignpostOptions {
  source?: LSPayCnpSplitSignpostSource
  /** When false the hook skips the network call and all telemetry. Defaults to true. */
  enabled?: boolean
}

interface LspayCnpOnboardingSignpostState {
  isLoading: boolean
  readFailed: boolean
  shouldShowSignpost: boolean
}

const getBaseTrackingPayload = (source: LSPayCnpSplitSignpostSource) => ({
  source,
  userAttributeName: LSPAY_CNP_ONBOARDING_ATTRIBUTE,
})

export const isLspayCnpOnboardingEligible = (
  attributes?: UserAttribute[] | null
) =>
  attributes?.some(
    attribute =>
      attribute.name === LSPAY_CNP_ONBOARDING_ATTRIBUTE &&
      attribute.value === 'true'
  ) ?? false

export const useLspayCnpOnboardingSignpost = ({
  source = 'cpModal',
  enabled = true,
}: UseLspayCnpOnboardingSignpostOptions = {}) => {
  const userManager = useUserManager()
  const openedTrackedRef = useRef(false)
  const [state, setState] = useState<LspayCnpOnboardingSignpostState>({
    isLoading: true,
    readFailed: false,
    shouldShowSignpost: false,
  })

  const userId = userManager.getLoggedInUser().id

  // Fetch user attributes and determine if the signpost should be shown.
  // Local storage is checked first so that dismissed users never see the
  // signpost again even if the server write failed on a previous session.
  useEffect(() => {
    if (!enabled) {
      setState({
        isLoading: false,
        readFailed: false,
        shouldShowSignpost: false,
      })
      return
    }

    let isMounted = true
    openedTrackedRef.current = false

    const localKey = getLspayCnpOnboardingLocalKey(userId)
    const signpostDismissed = getFromStorage(localKey)

    setState({
      isLoading: !signpostDismissed,
      readFailed: false,
      shouldShowSignpost: false,
    })

    // Short-circuit: if the user already dismissed on this device, skip the
    // network call entirely and treat as not eligible.
    if (signpostDismissed) {
      return
    }

    getUserAttributes(userId)
      .then(attributes => {
        if (!isMounted) {
          return
        }

        setState({
          isLoading: false,
          readFailed: false,
          shouldShowSignpost: isLspayCnpOnboardingEligible(attributes),
        })
      })
      .catch(() => {
        if (!isMounted) {
          return
        }

        setState({
          isLoading: false,
          readFailed: true,
          shouldShowSignpost: false,
        })
      })

    return () => {
      isMounted = false
    }
  }, [userId, enabled])

  // Track when the signpost is opened
  useEffect(() => {
    if (!enabled || !state.shouldShowSignpost || openedTrackedRef.current) {
      return
    }

    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      LSPAY_CNP_SIGNPOST_OPENED_EVENT,
      getBaseTrackingPayload(source)
    )
    openedTrackedRef.current = true
  }, [source, state.shouldShowSignpost, enabled])

  const dismissSignpost = useCallback(async () => {
    setState(current => ({
      ...current,
      shouldShowSignpost: false,
    }))

    // Persist dismissal locally immediately so subsequent modal opens skip
    // the network call, even if the server write below fails.
    saveToStorage(getLspayCnpOnboardingLocalKey(userId), true)

    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      LSPAY_CNP_SIGNPOST_DISMISSED_EVENT,
      {
        ...getBaseTrackingPayload(source),
        userAttributeValue: LSPAY_CNP_ONBOARDING_DISMISSED_VALUE,
      }
    )

    try {
      await setUserAttribute({
        user_id: userId,
        name: LSPAY_CNP_ONBOARDING_ATTRIBUTE,
        value: LSPAY_CNP_ONBOARDING_DISMISSED_VALUE,
      })
    } catch {
      trackUserEvent(
        WEB_REGISTER.DISPLAY_NAME,
        LSPAY_CNP_SIGNPOST_DISMISSAL_FAILED_EVENT,
        {
          ...getBaseTrackingPayload(source),
          userAttributeValue: LSPAY_CNP_ONBOARDING_DISMISSED_VALUE,
        }
      )
    }
  }, [source, userId])

  return {
    ...state,
    dismissSignpost,
  }
}
